import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import { collection, getDocs, query, where, getDoc, doc } from 'firebase/firestore';
import { getUserAllVideoProgress } from '../utils/videoUtils';
import { grantAdminTrainerSubscription } from '../utils/subscriptionService';
import { useTranslation } from 'react-i18next';
import { addToFavorites, removeFromFavorites, getUserFavoriteVideoIds } from '../services/favoriteService';
import { Box, Typography, Button, Chip, CircularProgress } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';

interface Video {
  id: string;
  title: string;
  description: string;
  videoUrl: string;
  thumbnailUrl: string;
  isIntro: boolean;
  requiredSubscription: boolean;
  module: number;
  category?: string;
  difficulty?: string;
  duration?: number;
  moduleOrder?: number; // Video number in module
  schede?: string; // Added for PDF link
}

const Dashboard: React.FC = () => {
  const { user, role } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const [introVideo, setIntroVideo] = useState<Video | null>(null);
  const [paidVideos, setPaidVideos] = useState<Video[]>([]);
  const [loading, setLoading] = useState(true);
  const [userModules, setUserModules] = useState<number[]>([]);
  const [hasActiveSub, setHasActiveSub] = useState(false);
  const [videoProgress, setVideoProgress] = useState<{ [videoId: string]: any }>({});

  // Add filter state
  const [selectedModule, setSelectedModule] = useState<number | 'all'>('all');
  const [selectedDifficulty, setSelectedDifficulty] = useState<string>('all');
  const [selectedVideoNumber, setSelectedVideoNumber] = useState<number | 'all'>('all');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
  
  // Favorite state
  const [favoriteVideoIds, setFavoriteVideoIds] = useState<Set<string>>(new Set());

  // Check if user is admin or trainer
  const isAdminOrTrainer = role === 'admin' || role === 'trainer';

  // Helper function to get video progress
  const getVideoProgress = (videoId: string) => {
    return videoProgress[videoId] || null;
  };

  // Favorite functions
  const toggleFavorite = async (video: Video, event: React.MouseEvent) => {
    event.stopPropagation(); // Prevent card click
    
    if (!user?.uid) return;
    
    try {
      const isFavorited = favoriteVideoIds.has(video.id);
      
      if (isFavorited) {
        await removeFromFavorites(user.uid, video.id);
        setFavoriteVideoIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(video.id);
          return newSet;
        });
      } else {
        await addToFavorites(user.uid, video.id, {
          title: video.title,
          module: video.module,
          category: video.category
        });
        setFavoriteVideoIds(prev => new Set([...prev, video.id]));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Helper function to format watchtime
  const formatWatchtime = (seconds: number) => {
    // Handle invalid input
    if (!seconds || isNaN(seconds) || seconds < 0) {
      return '0s';
    }
    
    if (seconds < 60) {
      return `${Math.floor(seconds)}s`;
    } else if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${minutes}m ${remainingSeconds}s`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const remainingSeconds = Math.floor(seconds % 60);
      return `${hours}h ${minutes}m ${remainingSeconds}s`;
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      
    // 1. Fetch intro video
    const introQ = query(collection(db, 'videos'), where('isIntro', '==', true));
      const introSnapshot = await getDocs(introQ);
      if (!introSnapshot.empty) {
        const introData = introSnapshot.docs[0].data() as Video;
        setIntroVideo({ ...introData, id: introSnapshot.docs[0].id });
    } else {
      setIntroVideo(null);
    }
    // 2. Fetch user subscription/unlocked modules
    if (user) {
      const userDoc = await getDoc(doc(db, 'users', user.uid));
        const data = userDoc.data();
        setUserModules(data?.subscription?.unlockedModules || []);
        setHasActiveSub(!!data?.subscription?.active);
        
        // Grant default admin/trainer subscription if needed
        if (isAdminOrTrainer) {
          grantAdminTrainerSubscription(user.uid).catch((err: any) => {
            console.error('Error granting admin/trainer subscription:', err);
          });
        }
    }
    // 3. Fetch paid videos (not intro)
    const paidQ = query(collection(db, 'videos'), where('requiredSubscription', '==', true), where('isIntro', '==', false));
      const paidSnapshot = await getDocs(paidQ);
      const paidData = paidSnapshot.docs.map(doc => ({ ...doc.data(), id: doc.id } as Video));
      setPaidVideos(paidData);
      
    setLoading(false);
  };

    loadData();
  }, [user]);

  // Load video progress - using same calculation as VideoPlayer (lastPosition instead of watchTime)
  useEffect(() => {
  const loadVideoProgress = async () => {
      if (user?.uid) {
    try {
      const progressData = await getUserAllVideoProgress(user.uid);
          const progressMap: { [videoId: string]: any } = {};
      
          progressData.forEach((progress: any) => {
        // Use same calculation as VideoPlayer: lastPosition / duration
        let percentWatched = 0;
            if (progress.duration && progress.duration > 0 && progress.lastPosition >= 0) {
              percentWatched = Math.round((progress.lastPosition / progress.duration) * 100);
            }
        
        progressMap[progress.videoId] = {
              ...progress,
              percentWatched,
              // Use completed field directly from progress (set at 95% threshold in useVideoTracker)
              completed: progress.completed || false
        };
      });
      
      setVideoProgress(progressMap);
    } catch (error) {
      console.error('Error loading video progress:', error);
        }
    }
  };

    loadVideoProgress();
  }, [user?.uid]);

  // Load user favorites
  useEffect(() => {
    const loadFavorites = async () => {
      if (user?.uid) {
        try {
          const favoriteIds = await getUserFavoriteVideoIds(user.uid);
          setFavoriteVideoIds(favoriteIds);
        } catch (error) {
          console.error('Error loading favorites:', error);
        }
      }
    };

    loadFavorites();
  }, [user?.uid]);

  // Refresh intro video progress when component mounts or user changes
  useEffect(() => {
    if (introVideo?.id && user?.uid) {
      console.log('Intro video progress refreshed (cookie-based)');
    }
  }, [introVideo?.id, user?.uid]);

  // Refresh intro video progress when user navigates to dashboard
  useEffect(() => {
    if (location.pathname === '/dashboard' && introVideo?.id && user?.uid) {
      console.log('User navigated to Dashboard, refreshing intro video progress');
    }
  }, [location.pathname, introVideo?.id, user?.uid]);

  // Get unlocked videos based on user subscription
  const unlockedVideos = isAdminOrTrainer ? paidVideos : paidVideos.filter(v => userModules.includes(v.module));

  // Get filter options
  const moduleOptions = Array.from(new Set(unlockedVideos.map(v => v.module))).sort((a, b) => a - b);
  const difficultyOptions = Array.from(new Set(unlockedVideos.map(v => v.difficulty).filter(Boolean)));

  // Filter videos by selected module, difficulty, video number, and favorites
  let filteredVideos = unlockedVideos;
  if (selectedModule !== 'all') {
    filteredVideos = filteredVideos.filter(v => v.module === selectedModule);
  }
  if (selectedDifficulty !== 'all') {
    filteredVideos = filteredVideos.filter(v => 
      v.difficulty && v.difficulty.toLowerCase() === selectedDifficulty.toLowerCase()
    );
  }
  if (showFavoritesOnly) {
    filteredVideos = filteredVideos.filter(v => favoriteVideoIds.has(v.id));
  }
  // Only show video number dropdown if a module is selected
  const videoNumberOptions = selectedModule !== 'all'
    ? Array.from(new Set(unlockedVideos.filter(v => v.module === selectedModule).map(v => v.moduleOrder))).sort((a, b) => (a ?? 0) - (b ?? 0))
    : [];
  if (selectedVideoNumber !== 'all' && selectedModule !== 'all') {
    filteredVideos = filteredVideos.filter(v => v.moduleOrder === selectedVideoNumber);
  }
  // Sort by module, then moduleOrder
  filteredVideos = filteredVideos.sort((a, b) => {
    if (a.module !== b.module) return a.module - b.module;
    return (a.moduleOrder ?? 0) - (b.moduleOrder ?? 0);
  });

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(180deg, rgba(15, 15, 20, 0.95) 0%, rgba(25, 25, 35, 0.98) 100%)',
      position: 'relative',
      overflowX: 'hidden'
    }}>
      {/* Background Image with Overlay */}
      <Box sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundImage: 'url(/martinbg2.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        opacity: 0.15,
        zIndex: 0
      }} />
      
      {/* Main Container - Editorial Layout */}
      <Box sx={{
        width: '100%', 
        maxWidth: 1400,
        mx: 'auto',
        position: 'relative',
        zIndex: 1,
        pl: { xs: 2, md: 4 },
        pr: { xs: 2.5, md: 4 },
        overflowX: 'hidden',
        boxSizing: 'border-box'
      }}>
        {/* Editorial Header - Minimal Top Bar */}
        <Box sx={{ 
          pt: { xs: 4, md: 6 },
          pb: { xs: 3, md: 4 },
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
          gap: 2
        }}>
          {/* Welcome Text - Typography Focused */}
          <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 auto' } }}>
            <Typography variant="h1" sx={{ 
              fontWeight: 300, 
              color: '#ffffff',
              fontSize: { xs: '2.5rem', md: '4rem' },
              letterSpacing: '-0.03em',
              lineHeight: 1.1,
              mb: 1,
              wordWrap: 'break-word',
              overflowWrap: 'break-word',
              maxWidth: '100%'
            }}>
              {t('dashboard.welcome', { name: user?.displayName?.split(' ')[0] || t('common.user') })}
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'rgba(255, 255, 255, 0.6)',
              fontWeight: 300,
              fontSize: { xs: '0.9rem', md: '1.1rem' },
              letterSpacing: '0.02em'
            }}>
              {t('common.readyToStartFitnessJourney')}
            </Typography>
          </Box>

          {/* Minimal Filter Pills - Modern Tag Style */}
          {(isAdminOrTrainer || (hasActiveSub && unlockedVideos.length > 0)) && (
            <Box sx={{ 
              display: 'flex',
              gap: { xs: 0.75, sm: 1 },
              flexWrap: 'wrap',
              alignItems: 'center',
              mt: { xs: 3, md: 0 },
              width: '100%',
              maxWidth: '100%'
            }}>
              {/* Module Filter */}
              <Box sx={{ position: 'relative', minWidth: { xs: '80px', sm: '100px' }, flex: { xs: '1 1 auto', sm: '0 0 auto' }, maxWidth: { xs: '100%', sm: 'none' } }}>
                <select 
                  value={selectedModule} 
                  onChange={e => setSelectedModule(e.target.value === 'all' ? 'all' : Number(e.target.value))} 
                  style={{ 
                    padding: '8px 28px 8px 14px',
                    borderRadius: 20,
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    background: selectedModule !== 'all' ? 'rgba(229, 57, 53, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                    color: 'transparent',
                    fontSize: '0.75rem',
                    outline: 'none',
                    cursor: 'pointer',
                    fontWeight: 500,
                    backdropFilter: 'blur(10px)',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    transition: 'all 0.2s ease',
                    width: '100%',
                    height: '32px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = selectedModule !== 'all' ? 'rgba(229, 57, 53, 0.25)' : 'rgba(255, 255, 255, 0.12)';
                    e.currentTarget.style.borderColor = 'rgba(229, 57, 53, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = selectedModule !== 'all' ? 'rgba(229, 57, 53, 0.15)' : 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                  }}
                >
                  <option value="all" style={{ background: '#1a1a1f', color: '#fff' }}>{t('dashboard.allModules')}</option>
                  {moduleOptions.map(m => <option key={m} value={m} style={{ background: '#1a1a1f', color: '#fff' }}>{t('dashboard.moduleLabel', { number: m })}</option>)}
                </select>
                {/* Display overlay */}
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 1.75,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  textAlign: 'center'
                }}>
                  {selectedModule !== 'all' ? t('dashboard.moduleLabel', { number: selectedModule }) : 'Module'}
                  <Box sx={{
                    position: 'absolute',
                    right: 8,
                    width: 0,
                    height: 0,
                    borderLeft: '4px solid transparent',
                    borderRight: '4px solid transparent',
                    borderTop: '4px solid rgba(255, 255, 255, 0.7)',
                    pointerEvents: 'none'
                  }} />
                </Box>
              </Box>
              
              {/* Difficulty Filter */}
              <Box sx={{ position: 'relative', minWidth: { xs: '80px', sm: '100px' }, flex: { xs: '1 1 auto', sm: '0 0 auto' }, maxWidth: { xs: '100%', sm: 'none' } }}>
                <select 
                  value={selectedDifficulty} 
                  onChange={e => setSelectedDifficulty(e.target.value)} 
                  style={{ 
                    padding: '8px 28px 8px 14px',
                    borderRadius: 20,
                    border: '1px solid rgba(255, 255, 255, 0.15)',
                    background: selectedDifficulty !== 'all' ? 'rgba(229, 57, 53, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                    color: 'transparent',
                    fontSize: '0.75rem',
                    outline: 'none',
                    cursor: 'pointer',
                    fontWeight: 500,
                    backdropFilter: 'blur(10px)',
                    appearance: 'none',
                    WebkitAppearance: 'none',
                    MozAppearance: 'none',
                    transition: 'all 0.2s ease',
                    width: '100%',
                    height: '32px'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = selectedDifficulty !== 'all' ? 'rgba(229, 57, 53, 0.25)' : 'rgba(255, 255, 255, 0.12)';
                    e.currentTarget.style.borderColor = 'rgba(229, 57, 53, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = selectedDifficulty !== 'all' ? 'rgba(229, 57, 53, 0.15)' : 'rgba(255, 255, 255, 0.08)';
                    e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                  }}
                >
                  <option value="all" style={{ background: '#1a1a1f', color: '#fff' }}>{t('dashboard.allDifficulties')}</option>
                  {difficultyOptions.map(d => d ? <option key={d} value={d} style={{ background: '#1a1a1f', color: '#fff' }}>{d.charAt(0).toUpperCase() + d.slice(1).toLowerCase()}</option> : null)}
                </select>
                {/* Display overlay */}
                <Box sx={{
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  right: 0,
                  transform: 'translateY(-50%)',
                  pointerEvents: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  px: 1.75,
                  fontSize: '0.75rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  textAlign: 'center'
                }}>
                  {selectedDifficulty !== 'all' ? (selectedDifficulty.charAt(0).toUpperCase() + selectedDifficulty.slice(1).toLowerCase()) : 'Difficulty'}
                  <Box sx={{
                    position: 'absolute',
                    right: 8,
                    width: 0,
                    height: 0,
                    borderLeft: '4px solid transparent',
                    borderRight: '4px solid transparent',
                    borderTop: '4px solid rgba(255, 255, 255, 0.7)',
                    pointerEvents: 'none'
                  }} />
                </Box>
              </Box>

              {/* Video Number Filter - Conditional */}
              {selectedModule !== 'all' && (
                <Box sx={{ position: 'relative', minWidth: { xs: '80px', sm: '100px' }, flex: { xs: '1 1 auto', sm: '0 0 auto' }, maxWidth: { xs: '100%', sm: 'none' } }}>
                  <select 
                    value={selectedVideoNumber} 
                    onChange={e => setSelectedVideoNumber(e.target.value === 'all' ? 'all' : Number(e.target.value))} 
                    style={{ 
                      padding: '8px 28px 8px 14px',
                      borderRadius: 20,
                      border: '1px solid rgba(255, 255, 255, 0.15)',
                      background: selectedVideoNumber !== 'all' ? 'rgba(229, 57, 53, 0.15)' : 'rgba(255, 255, 255, 0.08)',
                      color: 'transparent',
                      fontSize: '0.75rem',
                      outline: 'none',
                      cursor: 'pointer',
                      fontWeight: 500,
                      backdropFilter: 'blur(10px)',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      MozAppearance: 'none',
                      transition: 'all 0.2s ease',
                      width: '100%',
                      height: '32px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = selectedVideoNumber !== 'all' ? 'rgba(229, 57, 53, 0.25)' : 'rgba(255, 255, 255, 0.12)';
                      e.currentTarget.style.borderColor = 'rgba(229, 57, 53, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = selectedVideoNumber !== 'all' ? 'rgba(229, 57, 53, 0.15)' : 'rgba(255, 255, 255, 0.08)';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.15)';
                    }}
                  >
                    <option value="all" style={{ background: '#1a1a1f', color: '#fff' }}>{t('dashboard.allVideoNumbers')}</option>
                    {videoNumberOptions.map(n => <option key={n} value={n} style={{ background: '#1a1a1f', color: '#fff' }}>{t('dashboard.videoNumberLabel', { number: n })}</option>)}
                  </select>
                  {/* Display overlay */}
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: 0,
                    right: 0,
                    transform: 'translateY(-50%)',
                    pointerEvents: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    px: 1.75,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    color: '#ffffff',
                    textAlign: 'center'
                  }}>
                    {selectedVideoNumber !== 'all' ? t('dashboard.videoNumberLabel', { number: selectedVideoNumber }) : t('dashboard.allVideoNumbers')}
                    <Box sx={{
                      position: 'absolute',
                      right: 8,
                      width: 0,
                      height: 0,
                      borderLeft: '4px solid transparent',
                      borderRight: '4px solid transparent',
                      borderTop: '4px solid rgba(255, 255, 255, 0.7)',
                      pointerEvents: 'none'
                    }} />
                  </Box>
                </Box>
              )}

              {/* Favorites Filter */}
              <Chip
                label={showFavoritesOnly ? t('dashboard.favoritesOnly') : t('dashboard.allVideos')}
                onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                sx={{
                  bgcolor: showFavoritesOnly ? 'rgba(229, 57, 53, 0.2)' : 'rgba(255, 255, 255, 0.08)',
                  color: showFavoritesOnly ? '#ffffff' : 'rgba(255, 255, 255, 0.7)',
                  border: '1px solid rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  cursor: 'pointer',
                  '&:hover': { 
                    bgcolor: 'rgba(229, 57, 53, 0.25)',
                    borderColor: 'rgba(229, 57, 53, 0.4)'
                  },
                  fontSize: '0.75rem',
                  height: '32px',
                  fontWeight: 500,
                  transition: 'all 0.2s ease',
                  borderRadius: '20px',
                  px: 1.75
                }}
              />
            </Box>
          )}
        </Box>

        {loading ? (
          <Box sx={{ 
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '400px',
            py: 8
          }}>
            <CircularProgress sx={{ color: '#E53935' }} />
          </Box>
        ) : (
          <>
            {/* Hero Video Section - Editorial Magazine Style */}
            {introVideo && (
              <Box sx={{ 
                mb: { xs: 4, md: 6 },
                position: 'relative',
                borderRadius: { xs: 2, md: 3 },
                overflow: 'hidden',
                cursor: 'pointer',
                transition: 'transform 0.3s ease',
                width: '100%',
                maxWidth: '100%',
                '&:hover': {
                  transform: 'translateY(-4px)'
                }
              }}
              onClick={() => navigate(`/video/${introVideo.id}`)}
            >
              {/* Hero Image with Overlay */}
              {introVideo.thumbnailUrl && (
                <Box sx={{
                  position: 'relative',
                  width: '100%',
                  height: { xs: '300px', md: '500px' },
                  backgroundImage: `url(${introVideo.thumbnailUrl})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  backgroundRepeat: 'no-repeat',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.3) 50%, rgba(0,0,0,0.8) 100%)',
                    zIndex: 1
                  }
                }}>
                  {/* Play Button - Centered Overlay */}
                  <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 2,
                    width: { xs: 70, md: 100 },
                    height: { xs: 70, md: 100 },
                    borderRadius: '50%',
                    bgcolor: 'rgba(229, 57, 53, 0.9)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      bgcolor: '#E53935',
                      transform: 'translate(-50%, -50%) scale(1.1)'
                    },
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                  }}>
                    <PlayArrowIcon sx={{ fontSize: { xs: 35, md: 50 }, color: 'white', ml: 0.5 }} />
                  </Box>
                </Box>
              )}
              
              {/* Content Overlay - Bottom */}
              <Box sx={{
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                zIndex: 2,
                p: { xs: 3, md: 5 },
                background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.7) 100%)'
              }}>
                <Typography variant="h3" sx={{
                  color: '#ffffff',
                  fontWeight: 700,
                  mb: 1,
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  textShadow: '0 2px 10px rgba(0,0,0,0.5)',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '100%'
                }}>
                  {t('common.featuredVideo')}
                </Typography>
                <Typography variant="body1" sx={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: { xs: '0.9rem', md: '1.1rem' },
                  fontWeight: 300,
                  mb: 2,
                  textShadow: '0 1px 5px rgba(0,0,0,0.5)',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '100%'
                }}>
                  {t('dashboard.startYourFitnessJourney')}
                </Typography>
                
                {/* Progress Bar - Minimal */}
                {(() => {
                  const introProgress = getVideoProgress(introVideo.id);
                  if (introProgress && introProgress.duration > 0) {
                    const introPercentWatched = introProgress.percentWatched || 0;
                    return (
                      <Box sx={{ mt: 2 }}>
                        <Box sx={{
                          width: '100%',
                          height: 4,
                          background: 'rgba(255, 255, 255, 0.2)',
                          borderRadius: 2,
                          overflow: 'hidden'
                        }}>
                          <Box sx={{
                            width: `${introPercentWatched}%`,
                            height: '100%',
                            background: 'linear-gradient(90deg, #E53935, #FF6B35)',
                            transition: 'width 0.3s ease'
                          }} />
                        </Box>
                        <Typography variant="caption" sx={{
                          color: 'rgba(255, 255, 255, 0.8)',
                          fontSize: '0.7rem',
                          mt: 1,
                          display: 'block'
                        }}>
                          {introPercentWatched}% {t('common.watched')}
                        </Typography>
                      </Box>
                    );
                  }
                  return null;
                })()}
              </Box>
              </Box>
            )}

            {/* Video Library - Editorial Grid */}
            {(isAdminOrTrainer || (hasActiveSub && unlockedVideos.length > 0)) && filteredVideos.length > 0 && (
              <Box sx={{ mb: 6 }}>
                {/* Section Header */}
                <Typography variant="h2" sx={{
                  color: '#ffffff',
                  fontWeight: 300,
                  fontSize: { xs: '1.75rem', md: '2.5rem' },
                  mb: 4,
                  letterSpacing: '-0.02em',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '100%'
                }}>
                  {isAdminOrTrainer ? t('common.premiumVideoLibrary') : t('dashboard.yourUnlockedVideos')}
                </Typography>

                {/* Video Grid - Magazine Style */}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: filteredVideos.length === 1 ? '1fr' : 'repeat(2, 1fr)',
                      md: filteredVideos.length === 1 ? '1fr' : filteredVideos.length === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)',
                      lg: filteredVideos.length === 1 ? '1fr' : filteredVideos.length === 2 ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)'
                    },
                    gap: { xs: 2, md: 4 },
                    justifyContent: 'center',
                    width: '100%',
                    maxWidth: '100%',
                    boxSizing: 'border-box'
                  }}
                >
                  {filteredVideos.map((v) => (
                    <Box 
                      key={v.id}
                      onClick={() => navigate(`/video/${v.id}`)}
                      sx={{ 
                        cursor: 'pointer', 
                        position: 'relative',
                        borderRadius: { xs: 2, md: 3 },
                        overflow: 'hidden',
                        transition: 'all 0.3s ease',
                        bgcolor: 'rgba(255, 255, 255, 0.05)',
                        border: '1px solid rgba(255, 255, 255, 0.08)',
                        backdropFilter: 'blur(20px)',
                        width: '100%',
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        '&:hover': { 
                          transform: 'translateY(-6px)',
                          boxShadow: '0 20px 60px rgba(229, 57, 53, 0.25)',
                          borderColor: 'rgba(229, 57, 53, 0.4)',
                          '& .play-overlay': {
                            opacity: 1,
                            transform: 'translate(-50%, -50%) scale(1.1)',
                            bgcolor: '#E53935'
                          },
                          '& .video-image': {
                            filter: 'brightness(0.9)'
                          }
                        }
                      }}
                    >
                      {/* Thumbnail */}
                      <Box sx={{ position: 'relative', width: '100%', height: { xs: '220px', md: '260px' }, overflow: 'hidden' }}>
                        {v.thumbnailUrl && (
                          <Box
                            className="video-image"
                            sx={{
                              width: '100%',
                              height: '100%',
                              backgroundImage: `url(${v.thumbnailUrl})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center',
                              backgroundRepeat: 'no-repeat',
                              transition: 'filter 0.3s ease',
                              filter: 'brightness(0.7)'
                            }}
                          />
                        )}
                        
                        {/* Gradient Overlay */}
                        <Box sx={{
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '60%',
                          background: 'linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.8) 100%)',
                          zIndex: 1
                        }} />

                        {/* Play Button */}
                        <Box
                          className="play-overlay"
                          sx={{
                            position: 'absolute',
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            zIndex: 2,
                            width: { xs: 56, md: 64 },
                            height: { xs: 56, md: 64 },
                            borderRadius: '50%',
                            bgcolor: 'rgba(229, 57, 53, 0.85)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            opacity: 0.85,
                            boxShadow: '0 4px 20px rgba(0,0,0,0.4)'
                          }}
                        >
                          <PlayArrowIcon sx={{ fontSize: { xs: 28, md: 32 }, color: 'white', ml: 0.5 }} />
                        </Box>
                        
                        {/* Favorite Button */}
                        <Box
                          onClick={(e) => toggleFavorite(v, e)}
                          sx={{
                            position: 'absolute',
                            top: 12,
                            left: 12,
                            zIndex: 3,
                            cursor: 'pointer',
                            p: 0.75,
                            borderRadius: '50%',
                            background: 'rgba(0, 0, 0, 0.5)',
                            backdropFilter: 'blur(10px)',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              background: 'rgba(0, 0, 0, 0.7)',
                              transform: 'scale(1.15)'
                            }
                          }}
                        >
                          {favoriteVideoIds.has(v.id) ? (
                            <StarIcon sx={{ fontSize: 20, color: '#FFD700' }} />
                          ) : (
                            <StarBorderIcon sx={{ fontSize: 20, color: 'white' }} />
                          )}
                        </Box>
                        
                        {/* Duration Badge */}
                        {v.duration && (
                          <Box sx={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            zIndex: 3,
                            bgcolor: 'rgba(0, 0, 0, 0.75)',
                            color: 'white',
                            px: 1.5,
                            py: 0.75,
                            borderRadius: 2,
                            fontSize: '0.7rem',
                            fontWeight: 600,
                            backdropFilter: 'blur(10px)'
                          }}>
                            {Math.floor(v.duration / 60)}:{(v.duration % 60).toString().padStart(2, '0')}
                          </Box>
                        )}
                      </Box>
                      
                      {/* Content */}
                      <Box sx={{ p: { xs: 2.5, md: 3 }, bgcolor: 'rgba(255, 255, 255, 0.03)', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                        <Typography variant="h6" sx={{ 
                          mb: 1.5, 
                          color: '#ffffff', 
                          fontWeight: 600,
                          lineHeight: 1.3,
                          fontSize: { xs: '1rem', md: '1.1rem' },
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          maxWidth: '100%'
                        }}>
                          {v.title}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                          <Chip 
                            label={t('dashboard.moduleLabel', { number: v.module }) + (v.moduleOrder ? ` · ${t('dashboard.videoNumberLabel', { number: v.moduleOrder })}` : '')} 
                            size="small" 
                            sx={{ 
                              bgcolor: 'rgba(229, 57, 53, 0.15)',
                              color: '#E53935',
                              fontWeight: 600,
                              border: '1px solid rgba(229, 57, 53, 0.2)',
                              fontSize: '0.7rem',
                              height: '24px'
                            }}
                          />
                          {v.difficulty && (
                            <Chip 
                              label={v.difficulty.charAt(0).toUpperCase() + v.difficulty.slice(1).toLowerCase()} 
                              size="small" 
                              sx={{ 
                                bgcolor: (() => {
                                  const d = v.difficulty.toLowerCase();
                                  if (d === 'beginner') return 'rgba(76, 175, 80, 0.15)';
                                  if (d === 'intermediate') return 'rgba(255, 193, 7, 0.15)';
                                  if (d === 'advanced' || d === 'hard') return 'rgba(244, 67, 54, 0.15)';
                                  return 'rgba(255, 255, 255, 0.15)';
                                })(),
                                color: (() => {
                                  const d = v.difficulty.toLowerCase();
                                  if (d === 'beginner') return '#4CAF50';
                                  if (d === 'intermediate') return '#FFC107';
                                  if (d === 'advanced' || d === 'hard') return '#F44336';
                                  return '#FFFFFF';
                                })(),
                                fontWeight: 600,
                                border: (() => {
                                  const d = v.difficulty.toLowerCase();
                                  if (d === 'beginner') return '1px solid rgba(76, 175, 80, 0.25)';
                                  if (d === 'intermediate') return '1px solid rgba(255, 193, 7, 0.25)';
                                  if (d === 'advanced' || d === 'hard') return '1px solid rgba(244, 67, 54, 0.25)';
                                  return '1px solid rgba(255, 255, 255, 0.2)';
                                })(),
                                fontSize: '0.7rem',
                                height: '24px'
                              }}
                            />
                          )}
                        </Box>
                        
                        {v.category && (
                          <Typography variant="caption" sx={{ 
                            color: 'rgba(255, 255, 255, 0.6)', 
                            fontWeight: 400, 
                            fontSize: '0.75rem',
                            display: 'block',
                            mb: 1.5,
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            maxWidth: '100%'
                          }}>
                            {v.category}
                          </Typography>
                        )}
                        
                        {/* PDF Link */}
                        {v.schede && (
                          <Button
                            variant="outlined"
                            size="small"
                            href={v.schede}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={(e) => e.stopPropagation()}
                            sx={{ 
                              borderRadius: 2, 
                              fontWeight: 600,
                              borderColor: 'rgba(76, 175, 80, 0.4)',
                              color: '#4CAF50',
                              fontSize: '0.7rem',
                              py: 0.5,
                              px: 1.5,
                              minWidth: 'auto',
                              '&:hover': {
                                borderColor: '#4CAF50',
                                color: '#4CAF50',
                                background: 'rgba(76, 175, 80, 0.1)'
                              }
                            }}
                          >
                            {t('common.viewPdf')}
                          </Button>
                        )}
                        
                        {/* Progress Bar - Minimal */}
                        {(() => {
                          const progress = getVideoProgress(v.id);
                          if (progress && (progress.percentWatched > 0 || progress.watchTime > 0)) {
                            return (
                              <Box sx={{ mt: 2 }}>
                                <Box sx={{
                                  width: '100%',
                                  height: 3,
                                  background: 'rgba(255, 255, 255, 0.1)',
                                  borderRadius: 2,
                                  overflow: 'hidden'
                                }}>
                                  <Box sx={{
                                    width: `${progress.percentWatched}%`,
                                    height: '100%',
                                    background: progress.completed 
                                      ? 'linear-gradient(90deg, #4CAF50, #66BB6A)' 
                                      : 'linear-gradient(90deg, #E53935, #FF6B35)',
                                    transition: 'width 0.3s ease'
                                  }} />
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.75 }}>
                                  <Typography variant="caption" sx={{
                                    color: progress.completed ? '#4CAF50' : '#E53935',
                                    fontWeight: 600,
                                    fontSize: '0.65rem'
                                  }}>
                                    {progress.completed ? t('common.completed') : `${progress.percentWatched}%`}
                                  </Typography>
                                  <Typography variant="caption" sx={{
                                    color: 'rgba(255, 255, 255, 0.5)',
                                    fontSize: '0.65rem'
                                  }}>
                                    {formatWatchtime(progress.watchTime || 0)}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          }
                          return null;
                        })()}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* Premium CTA - Editorial Style */}
            {!(isAdminOrTrainer || (hasActiveSub && unlockedVideos.length > 0)) && (
              <Box sx={{
                mt: 8,
                mb: 6,
                textAlign: 'center',
                p: { xs: 3, md: 6 },
                borderRadius: 3,
                background: 'linear-gradient(135deg, rgba(229, 57, 53, 0.1) 0%, rgba(229, 57, 53, 0.05) 100%)',
                border: '1px solid rgba(229, 57, 53, 0.2)',
                backdropFilter: 'blur(20px)',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}>
                <Typography variant="h2" sx={{
                  color: '#ffffff',
                  fontWeight: 300,
                  fontSize: { xs: '2rem', md: '3rem' },
                  mb: 2,
                  letterSpacing: '-0.02em',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '100%',
                  px: { xs: 1, md: 0 }
                }}>
                  {t('common.unlockPremiumContent')}
                </Typography>
                <Typography variant="h6" sx={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontWeight: 300,
                  fontSize: { xs: '1rem', md: '1.2rem' },
                  mb: 4,
                  maxWidth: { xs: '100%', md: 600 },
                  mx: 'auto',
                  lineHeight: 1.6,
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  px: { xs: 1, md: 0 }
                }}>
                  {t('common.getAccessToCompleteLibrary')}
                </Typography>
                <Button 
                  variant="contained" 
                  sx={{ 
                    bgcolor: '#E53935',
                    color: 'white',
                    fontWeight: 600,
                    px: { xs: 4, md: 6 },
                    py: { xs: 1.5, md: 2 },
                    fontSize: { xs: '0.9rem', md: '1rem' },
                    borderRadius: 3,
                    textTransform: 'none',
                    '&:hover': { 
                      bgcolor: '#C62828',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(229, 57, 53, 0.4)'
                    },
                    transition: 'all 0.3s ease'
                  }}
                  onClick={() => navigate('/dashboard')}
                >
                  {t('common.explorePlans')}
                </Button>
                <Typography variant="body2" sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  mt: 3,
                  fontSize: '0.85rem'
                }}>
                  {t('common.startWithFreeIntro')}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;